import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { cr } from '@/lib/db';
import { EmptyState } from '@/components/EmptyState';
import { Loader2, ArrowRightLeft, Sparkles } from 'lucide-react';
import { invokeAscAi, aiHeaderBadge } from '@/components/ai/AscAiPanels';

// ── HandoffPanel (B4 minimal-real, 2026-08-15) ───────────────────────────────
// Stage-to-stage clinical handoff in SBAR form, persisted to cr.case_handoffs
// (RLS: MFA + case access + given_by = auth.uid()). given_by_name resolves
// from the caller's org membership — never free-typed.

const STAGES = ['preop', 'inor', 'pacu', 'discharge', 'ward'] as const;
const LABEL: Record<string, string> = { preop: 'Pre-op', inor: 'OR', pacu: 'PACU', discharge: 'Discharge', ward: 'Ward' };

interface Handoff {
    handoff_id: string;
    from_stage: string;
    to_stage: string;
    situation: string;
    background: string | null;
    assessment: string | null;
    recommendation: string | null;
    given_by_name: string;
    received_by_name: string | null;
    created_at: string;
}

export function HandoffPanel({ caseId, orgId, encounterId, currentStage }: {
    caseId: number; orgId: string; encounterId: number | null; currentStage: string | null;
}) {
    const qc = useQueryClient();
    const stageIdx = STAGES.indexOf((currentStage ?? 'preop') as (typeof STAGES)[number]);
    const [fromStage, setFromStage] = useState<string>(stageIdx >= 0 ? STAGES[stageIdx] : 'inor');
    const [toStage, setToStage] = useState<string>(STAGES[Math.min(Math.max(stageIdx, 0) + 1, STAGES.length - 1)]);
    const [situation, setSituation] = useState('');
    const [background, setBackground] = useState('');
    const [assessment, setAssessment] = useState('');
    const [recommendation, setRecommendation] = useState('');
    // AI SBAR draft (2026-08-15): fills the EDITABLE fields from the documented
    // PACU/Intra-Op record only. The clinician reviews/edits, then records —
    // the draft itself never writes a handoff.
    const [aiBusy, setAiBusy] = useState(false);
    const [aiNote, setAiNote] = useState('');

    async function draftSbar() {
        setAiBusy(true); setAiNote('');
        try {
            const res = await invokeAscAi<{ ok: boolean; empty?: boolean; message?: string; draft?: { situation?: string; background?: string; assessment?: string; recommendation?: string } }>({
                action: 'sbar-draft', case_id: caseId,
            });
            if (res.empty) { setAiNote(res.message ?? 'Nothing documented to draft from yet.'); return; }
            const d = res.draft ?? {};
            if (d.situation) setSituation(d.situation);
            if (d.background) setBackground(d.background);
            if (d.assessment) setAssessment(d.assessment);
            if (d.recommendation) setRecommendation(d.recommendation);
            setAiNote('Draft filled from the documented record — review and edit before recording the handoff.');
        } catch (e) {
            setAiNote(e instanceof Error ? e.message : 'SBAR draft unavailable');
        } finally {
            setAiBusy(false);
        }
    }
    const [receivedBy, setReceivedBy] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const handoffs = useQuery({
        queryKey: ['case-handoffs', caseId],
        queryFn: async (): Promise<Handoff[]> => {
            const { data, error } = await cr().from('case_handoffs')
                .select('handoff_id, from_stage, to_stage, situation, background, assessment, recommendation, given_by_name, received_by_name, created_at')
                .eq('case_id', caseId)
                .order('created_at', { ascending: false });
            if (error) throw error;
            return (data ?? []) as Handoff[];
        },
    });

    async function submit() {
        setErr('');
        if (!situation.trim()) { setErr('Situation is required'); return; }
        if (fromStage === toStage) { setErr('From and to stages must differ'); return; }
        setBusy(true);
        try {
            const { data: authData } = await supabase.auth.getUser();
            if (!authData.user) { setErr('No authenticated session'); return; }
            const { data: member } = await supabase.schema('saas').from('org_members')
                .select('full_name, first_name, last_name')
                .eq('org_id', orgId).eq('user_id', authData.user.id).limit(1).single();
            const givenByName = member?.full_name
                ?? [member?.first_name, member?.last_name].filter(Boolean).join(' ') ?? 'Unknown';

            const { error } = await cr().from('case_handoffs').insert({
                org_id: orgId,
                case_id: caseId,
                encounter_id: encounterId,
                from_stage: fromStage,
                to_stage: toStage,
                situation: situation.trim(),
                background: background.trim() || null,
                assessment: assessment.trim() || null,
                recommendation: recommendation.trim() || null,
                given_by_name: givenByName,
                received_by_name: receivedBy.trim() || null,
            });
            if (error) { setErr(`Save failed: ${error.message}`); return; }
            setSituation(''); setBackground(''); setAssessment(''); setRecommendation(''); setReceivedBy('');
            await qc.invalidateQueries({ queryKey: ['case-handoffs', caseId] });
        } finally {
            setBusy(false);
        }
    }

    const field = 'w-full rounded border border-pt-input-border bg-pt-darker text-pt-text text-sm px-3 py-2';
    return (
        <div className="max-w-3xl space-y-5">
            <div className="bg-pt-panel border border-pt-border rounded-md p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-pt-text [&>svg]:w-4 [&>svg]:h-4">
                    <ArrowRightLeft /> New handoff (SBAR)
                    <button type="button" disabled={aiBusy}
                        onClick={() => void draftSbar()}
                        className="ml-auto text-[11px] font-medium rounded border border-violet-400/40 text-violet-200 px-2.5 py-1.5 hover:bg-violet-500/10 disabled:opacity-50 flex items-center gap-1.5">
                        <Sparkles size={12} /> {aiBusy ? 'Reading the record…' : 'Draft from PACU / Intra-Op record'}
                    </button>
                    {aiHeaderBadge}
                </div>
                {aiNote && <p className="text-[10px] text-pt-text-muted">{aiNote}</p>}
                <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs text-pt-text-muted">From stage
                        <select value={fromStage} onChange={(e) => setFromStage(e.target.value)} className={`mt-1 ${field}`}>
                            {STAGES.map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
                        </select>
                    </label>
                    <label className="text-xs text-pt-text-muted">To stage
                        <select value={toStage} onChange={(e) => setToStage(e.target.value)} className={`mt-1 ${field}`}>
                            {STAGES.map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
                        </select>
                    </label>
                </div>
                <label className="block text-xs text-pt-text-muted">Situation (required)
                    <textarea value={situation} onChange={(e) => setSituation(e.target.value)} rows={2} className={`mt-1 ${field}`} />
                </label>
                <label className="block text-xs text-pt-text-muted">Background
                    <textarea value={background} onChange={(e) => setBackground(e.target.value)} rows={2} className={`mt-1 ${field}`} />
                </label>
                <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs text-pt-text-muted">Assessment
                        <textarea value={assessment} onChange={(e) => setAssessment(e.target.value)} rows={2} className={`mt-1 ${field}`} />
                    </label>
                    <label className="text-xs text-pt-text-muted">Recommendation
                        <textarea value={recommendation} onChange={(e) => setRecommendation(e.target.value)} rows={2} className={`mt-1 ${field}`} />
                    </label>
                </div>
                <label className="block text-xs text-pt-text-muted">Received by
                    <input value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} className={`mt-1 ${field}`} />
                </label>
                {err && <div className="text-sm text-pt-red">{err}</div>}
                <button type="button" onClick={submit} disabled={busy}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-pt-gold text-pt-darker text-sm font-semibold disabled:opacity-50 [&>svg]:w-4 [&>svg]:h-4">
                    {busy ? <Loader2 className="animate-spin" /> : <ArrowRightLeft />} Record handoff
                </button>
            </div>

            {handoffs.isLoading ? (
                <div className="text-sm text-pt-text-dim"><span className="pt-spinner mr-2" />Loading handoffs…</div>
            ) : (handoffs.data ?? []).length === 0 ? (
                <EmptyState title="No handoffs recorded for this case yet" hint="Use the form above to record the first stage-to-stage SBAR handoff." />
            ) : (
                <div className="space-y-3">
                    {(handoffs.data ?? []).map((h) => (
                        <div key={h.handoff_id} className="bg-pt-panel border border-pt-border rounded-md p-3">
                            <div className="flex items-center justify-between text-xs text-pt-text-muted">
                                <span className="font-semibold text-pt-cyan">{LABEL[h.from_stage] ?? h.from_stage} → {LABEL[h.to_stage] ?? h.to_stage}</span>
                                <span className="font-mono">{new Date(h.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                            </div>
                            <div className="text-sm mt-1.5">{h.situation}</div>
                            {h.background && <div className="text-xs text-pt-text-muted mt-1"><span className="font-semibold">B:</span> {h.background}</div>}
                            {h.assessment && <div className="text-xs text-pt-text-muted mt-0.5"><span className="font-semibold">A:</span> {h.assessment}</div>}
                            {h.recommendation && <div className="text-xs text-pt-text-muted mt-0.5"><span className="font-semibold">R:</span> {h.recommendation}</div>}
                            <div className="text-[11px] text-pt-text-dim mt-1.5">
                                Given by {h.given_by_name}{h.received_by_name ? ` · received by ${h.received_by_name}` : ''}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
